import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactPoints = [
  "Nous vous contacterons dans les 24 heures.",
  "Parlons de votre projet et commençons à élaborer.",
  "Un plan d'action en fonction des besoins et des objectifs de votre structure."
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    etablissement: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.prenom || !formData.nom || !formData.email || !formData.telephone || !formData.message) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === "success") {
        toast.success(response.data.message);
        setFormData({
          prenom: "",
          nom: "",
          email: "",
          telephone: "",
          etablissement: "",
          message: ""
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 sm:py-32 bg-slate-100/50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-sky-500 mb-6">
                PARLEZ-NOUS DE VOTRE PROJET
              </h2>
              <ul className="space-y-4">
                {contactPoints.map((point, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle size={24} weight="fill" className="text-sky-500 flex-shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <form 
              onSubmit={handleSubmit} 
              className="contact-form bg-white rounded-2xl shadow-lg p-8 space-y-6"
              data-testid="contact-form"
            >
              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="prenom" className="text-sky-600 font-medium">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    placeholder="Prénom"
                    className="border-slate-200 focus:border-sky-500"
                    data-testid="input-prenom"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom" className="text-sky-600 font-medium opacity-0">
                    Nom
                  </Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    placeholder="Nom"
                    className="border-slate-200 focus:border-sky-500"
                    data-testid="input-nom"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sky-600 font-medium">
                  E-mail <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="votre@email.fr"
                  className="border-slate-200 focus:border-sky-500"
                  data-testid="input-email"
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="telephone" className="text-sky-600 font-medium">
                  Téléphone <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="telephone"
                  name="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="+33 X XX XX XX XX"
                  className="border-slate-200 focus:border-sky-500"
                  data-testid="input-telephone"
                  required
                />
              </div>

              {/* Establishment */}
              <div className="space-y-2">
                <Label htmlFor="etablissement" className="text-sky-600 font-medium">
                  Établissement
                </Label>
                <Input
                  id="etablissement"
                  name="etablissement"
                  value={formData.etablissement}
                  onChange={handleChange}
                  placeholder="Nom de votre établissement"
                  className="border-slate-200 focus:border-sky-500"
                  data-testid="input-etablissement"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="text-sky-600 font-medium">
                  Commentaire ou message <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre projet ou votre besoin..."
                  rows={5}
                  className="border-slate-200 focus:border-sky-500 resize-none"
                  data-testid="input-message"
                  required
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-blue-700 hover:bg-blue-800 text-white w-full sm:w-auto px-8 py-3 font-medium"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? "Envoi en cours..." : "Envoyer"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
